import { Routes } from '@all-hands-hq/plex-graphql-schema';
import { Avatar, IconButton, Link as MuiLink, Slide, SlideProps, Snackbar, SvgIconTypeMap } from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PeopleIcon from '@material-ui/icons/People';
import Alert from '@material-ui/lab/Alert';
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { ReactComponent as BillSVG } from '../../../assets/bill.svg';
import { ReactComponent as IntegrateSVG } from '../../../assets/integrate.svg';
import { ReactComponent as LockSVG } from '../../../assets/lock.svg';
// TODO: uncomment when notifications get implemented
// import { ReactComponent as Notification } from '../../../assets/notification.svg';
import { ReactComponent as SettingsSVG } from '../../../assets/settings.svg';
import { ReactComponent as UserSettingSVG } from '../../../assets/user_setting.svg';
import { ReactComponent as UserSVG } from '../../../assets/user.svg';
import { AccessControlEnum, GetLoggedInUserQuery, OnboardingStep } from '../../../graphql/gen/types';
import { bottomBorderStyle, colorPalette, rightBorderStyle, spacings, TextSizesEnum } from '../../../theme';
import { PlexButtonHref } from '../../shared/primitives/PlexButtonHref';
import { PlexTypography } from '../../shared/primitives/PlexTypography';

interface Props {
  userProfile: NonNullable<GetLoggedInUserQuery['currentUser']>;
}

type Action = {
  label: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | OverridableComponent<SvgIconTypeMap<unknown, 'svg'>>;
  link: string;
  adminOnly: boolean;
};
const actions: Action[] = [
  {
    label: 'General Settings',
    icon: SettingsSVG,
    link: Routes.GENERAL_SETTINGS_PATH,
    adminOnly: false,
  },
  {
    label: 'Profile',
    icon: UserSVG,
    link: Routes.PROFILE_SETTINGS_PATH,
    adminOnly: false,
  },
  // TODO: uncomment when notifications get implemented
  // {
  //   label: 'Notifications',
  //   icon: Notification,
  //   link: Routes.NOTIFICATIONS_SETTINGS_PATH,
  //   adminOnly: false,
  // },
  {
    label: 'Account Settings',
    icon: UserSettingSVG,
    link: Routes.ACCOUNT_SETTINGS_PATH,
    adminOnly: true,
  },
  {
    label: 'Team',
    icon: PeopleIcon,
    link: Routes.TEAM_SETTINGS_PATH,
    adminOnly: true,
  },
  {
    label: 'Security',
    icon: LockSVG,
    link: Routes.SECURITY_SETTINGS_PATH,
    adminOnly: true,
  },
  {
    label: 'Integrations',
    icon: IntegrateSVG,
    link: Routes.INTEGRATIONS_SETTINGS_PATH,
    adminOnly: false,
  },
  {
    label: 'Usage & Billing',
    icon: BillSVG,
    link: Routes.BILLING_SETTINGS_PATH,
    adminOnly: true,
  },
];

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export const SettingsMenu = ({ userProfile }: Props): React.ReactElement => {
  const location = useLocation();
  const classes = useStyles();
  const onboardingDone = Object.keys(OnboardingStep).length === userProfile.completedOnboardingSteps.length;
  const [showOnboardingSuggestionSnackbar, setShowOnboardingSuggestionSnackbar] = useState(
    !onboardingDone && Boolean(userProfile.domain?.isBillingCurrent)
  );

  return (
    <div className={classes.settingsMenuContainer}>
      <div className={classes.avatarContainer}>
        <Avatar className={classes.avatar} alt={'avatar'} src={userProfile.picture} />
        <PlexTypography size={TextSizesEnum.Large} text={userProfile.name} />
        <MuiLink href={Routes.LOGOUT_API_PATH} className={classes.logout}>
          <PlexTypography size={TextSizesEnum.Normal} text={'Logout'} className={classes.logoutText} />
        </MuiLink>

        <Snackbar
          open={showOnboardingSuggestionSnackbar}
          TransitionComponent={SlideTransition}
          autoHideDuration={6000}
          onClose={() => {
            setShowOnboardingSuggestionSnackbar(false);
          }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <Alert
            severity="info"
            action={
              <>
                <PlexButtonHref size="small" label="Continue" href={Routes.ONBOARDING} />
                <IconButton
                  className={classes.snackbarClose}
                  aria-label="close"
                  color="inherit"
                  onClick={() => {
                    setShowOnboardingSuggestionSnackbar(false);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            You have not completed your onboarding.
          </Alert>
        </Snackbar>
      </div>
      {actions.map((action) => {
        if (userProfile.accessControl === AccessControlEnum.User && action.adminOnly) {
          return null;
        }
        const Icon = action.icon;
        return (
          <NavLink to={action.link} className={classes.link} key={action.link} activeClassName={classes.activeLink}>
            {action.link === location.pathname && <div className={classes.selectedItemHighlight} />}
            <Icon className={classes.menuItemIcon} />
            <PlexTypography text={action.label} size={TextSizesEnum.Large} className={classes.menuItemText} />
          </NavLink>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItemIcon: {
      width: 24,
      margin: '0 24px 0 32px',
    },
    menuItemText: {
      fontWeight: 600,
    },
    settingsMenuContainer: {
      width: 452,
      height: '100%',
      flexDirection: 'column',
      ...rightBorderStyle,
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 40,
    },
    avatarContainerInner: {
      position: 'relative',
      marginBottom: 24,
    },
    avatar: {
      width: 150,
      height: 150,
      borderRadius: 75,
    },
    logout: {
      paddingTop: 5,
      paddingBottom: 20,
    },
    logoutText: {
      color: colorPalette.primaryColorBlue._500,
      fontWeight: 900,
    },
    editAvatarButton: {
      right: 0,
      bottom: 0,
      position: 'absolute',
      backgroundColor: '#fff',
      border: `1px solid ${colorPalette.purpleGray._100}`,
      borderRadius: 22,
    },
    link: {
      textDecoration: 'none',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      height: 50,
      fontWeight: 600,
      color: colorPalette.purpleGray._500,
      '&:hover': {
        backgroundColor: colorPalette.purpleGray._100,
      },
      ...bottomBorderStyle,
      fill: `${colorPalette.purpleGray._500}`, // sets the svg color
    },
    activeLink: {
      color: colorPalette.primaryColorBlue._500,
      fill: `${colorPalette.primaryColorBlue._500}`, // sets the svg color
    },
    selectedItemHighlight: {
      position: 'absolute',
      top: 9,
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      width: 5,
      height: 28,
      backgroundColor: colorPalette.primaryColorBlue._500,
    },
    snackbarClose: {
      marginLeft: spacings.small,
    },
  })
);
